import { useState, useEffect, useRef, useCallback } from 'react';
import type { AutomationRecord, ZapierAutomationSettings } from '@sca-uk/shared';
import {
  fetchAutomationStatus,
  fetchPersonAutomations,
  fetchZapierAutomations,
} from '@lib/automations';

const POLL_INTERVAL_MS = 3_000;
const TERMINAL_STATUSES = new Set(['completed', 'failed']);

// ── Poll a single automation record until it reaches a terminal state ─────────

interface UseAutomationPollingResult {
  record: AutomationRecord | null;
  isPolling: boolean;
}

export function useAutomationPolling(
  recordId: string | null,
): UseAutomationPollingResult {
  const [record, setRecord] = useState<AutomationRecord | null>(null);
  const [isPolling, setIsPolling] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const activeIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!recordId) {
      setRecord(null);
      setIsPolling(false);
      return;
    }

    activeIdRef.current = recordId;
    setIsPolling(true);
    setRecord(null);

    let stopped = false;

    const poll = async () => {
      if (stopped || activeIdRef.current !== recordId) return;

      try {
        const result = await fetchAutomationStatus(recordId);
        if (activeIdRef.current !== recordId) return;

        setRecord(result);

        if (TERMINAL_STATUSES.has(result.status)) {
          setIsPolling(false);
          return;
        }
      } catch {
        // Transient error — keep polling
      }

      if (!stopped && activeIdRef.current === recordId) {
        timerRef.current = setTimeout(poll, POLL_INTERVAL_MS);
      }
    };

    poll();

    return () => {
      stopped = true;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [recordId]);

  return { record, isPolling };
}

// ── Fetch all automation records for a person ─────────────────────────────────

interface UsePersonAutomationsResult {
  records: AutomationRecord[];
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function usePersonAutomations(
  personId: number | null,
): UsePersonAutomationsResult {
  const [records, setRecords] = useState<AutomationRecord[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  const refresh = useCallback(() => setRefreshKey((k) => k + 1), []);

  useEffect(() => {
    if (!personId) {
      setRecords([]);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchPersonAutomations(personId)
      .then((data) => {
        if (!cancelled) setRecords(data);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load automation history');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, [personId, refreshKey]);

  return { records, isLoading, error, refresh };
}

// ── Fetch Zapier automation settings (for the trigger modal) ──────────────────

interface UseZapierAutomationsResult {
  settings: ZapierAutomationSettings | null;
  isLoading: boolean;
  error: string | null;
}

export function useZapierAutomations(): UseZapierAutomationsResult {
  const [settings, setSettings] = useState<ZapierAutomationSettings | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    fetchZapierAutomations()
      .then((data) => {
        if (!cancelled) setSettings(data);
      })
      .catch((err) => {
        if (!cancelled)
          setError(err instanceof Error ? err.message : 'Failed to load automations');
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => { cancelled = true; };
  }, []);

  return { settings, isLoading, error };
}
